/**
 * <zyv-gallery> — product photo slider.
 * Shows one photo at a time; shoppers move with arrows, swipe or thumbnails
 * (a vertical column on desktop). Thumbnails jump to
 * a photo; choosing a variant jumps to that variant's photo.
 *
 * Contract:
 *   [data-gallery-track]   scroll-snap container holding the slides
 *   [data-gallery-slide]   one per photo, carries data-media-id
 *   [data-gallery-prev] / [data-gallery-next]  arrow buttons
 *   [data-gallery-thumb]   thumbnail buttons, data-index
 *   [data-gallery-count]   "1 / 8" counter text
 *   script[data-gallery-variant-media]  JSON map { variantId: mediaId }
 */
class ZyvGallery extends HTMLElement {
  #index = 0;
  #timer = null;
  #paused = false;
  #scrollRaf = 0;

  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.count = this.querySelector('[data-gallery-count]');
    if (!this.track || this.slides.length === 0) return;

    try {
      const mapEl = this.querySelector('script[data-gallery-variant-media]');
      this.variantMedia = mapEl ? JSON.parse(mapEl.textContent) : {};
    } catch {
      this.variantMedia = {};
    }

    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.#userGo(this.#index - 1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.#userGo(this.#index + 1));
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.#userGo(Number(thumb.dataset.index)));
    });

    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#scrollRaf);
      this.#scrollRaf = requestAnimationFrame(() => this.#syncFromScroll());
    }, { passive: true });

    this.addEventListener('pointerenter', () => { this.#paused = true; });
    this.addEventListener('pointerleave', () => { this.#paused = false; });
    this.addEventListener('touchstart', () => this.#pauseFor(8000), { passive: true });
    this.addEventListener('focusin', () => { this.#paused = true; });
    this.addEventListener('focusout', () => { this.#paused = false; });

    // Variant change: jump to the chosen variant's photo.
    document.addEventListener('change', (event) => {
      const variantId = event.target?.dataset?.variantId
        || event.target?.selectedOptions?.[0]?.dataset?.variantId;
      if (!variantId) return;
      const mediaId = this.variantMedia[variantId];
      if (!mediaId) return;
      const target = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (target >= 0) this.#userGo(target);
    });

    // No autoplay: shoppers move through photos themselves with thumbnails, arrows or swipe.
    this.#render();
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
  }

  #pauseFor(ms) {
    this.#paused = true;
    clearTimeout(this.resumeTimeout);
    this.resumeTimeout = setTimeout(() => { this.#paused = false; }, ms);
  }

  #userGo(index) {
    this.#pauseFor(8000);
    this.#go(index);
  }

  #go(index) {
    const total = this.slides.length;
    this.#index = ((index % total) + total) % total;
    // Each slide is exactly one track width wide with no gap, so the offset is index × width.
    const isRtl = getComputedStyle(this.track).direction === 'rtl';
    const left = this.#index * this.track.clientWidth * (isRtl ? -1 : 1);
    this.track.scrollTo({ left, behavior: 'smooth' });
    this.#render();
  }

  #syncFromScroll() {
    const width = this.track.clientWidth || 1;
    const index = Math.round(Math.abs(this.track.scrollLeft) / width);
    if (index !== this.#index && index < this.slides.length) {
      this.#index = index;
      this.#render();
    }
  }

  #render() {
    this.thumbs.forEach((thumb, i) => {
      const active = i === this.#index;
      thumb.toggleAttribute('data-active', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
      if (active) {
        const strip = thumb.parentElement;
        if (strip && strip.scrollWidth > strip.clientWidth) {
          strip.scrollTo({ left: thumb.offsetLeft - strip.clientWidth / 2 + thumb.offsetWidth / 2, behavior: 'smooth' });
        }
      }
    });
    if (this.count) this.count.textContent = `${this.#index + 1} / ${this.slides.length}`;
  }
}

/**
 * <zyv-rail> — horizontal product slider with arrow buttons.
 *   [data-rail-track] scroll container, [data-rail-prev] / [data-rail-next] arrows.
 */
class ZyvRail extends HTMLElement {
  connectedCallback() {
    const track = this.querySelector('[data-rail-track]');
    if (!track) return;
    const step = () => track.clientWidth * 0.9;
    const dir = () => (getComputedStyle(track).direction === 'rtl' ? -1 : 1);
    this.querySelector('[data-rail-prev]')?.addEventListener('click', () => track.scrollBy({ left: -step() * dir(), behavior: 'smooth' }));
    this.querySelector('[data-rail-next]')?.addEventListener('click', () => track.scrollBy({ left: step() * dir(), behavior: 'smooth' }));
  }
}

if (!customElements.get('zyv-gallery')) customElements.define('zyv-gallery', ZyvGallery);
if (!customElements.get('zyv-rail')) customElements.define('zyv-rail', ZyvRail);
